import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { settingsApi } from '../utils/api';

const SettingsContext = createContext({
  vatRate: 18,
  paymentTrackingEnabled: true,
  // "מודול הוצאות והכנסות". Default FALSE before the fetch — the opposite of
  // paymentTrackingEnabled's true — because the module is off for most
  // restaurants: defaulting true would flash the two budget widgets on every
  // restaurant during load and yank them away when settings arrive.
  budgetModuleEnabled: false,
  // "עץ מוצר". Default FALSE before the fetch, same reason as
  // budgetModuleEnabled: the module is off for most restaurants, so defaulting
  // true would flash the nav link on every load and then yank it away.
  dishCostingEnabled: false,
  // "המוצרים המובילים ברכש". Default FALSE before the fetch, same reason as
  // the two flags above: off for most restaurants, so true would flash the
  // /analysis card on every load.
  topProductsEnabled: false,
  // False until the first GET /api/settings for the CURRENT restaurant
  // settles (success or failure) — see the render-time reset below. Lets a
  // consumer like RequireDishModule tell "not loaded yet" from "loaded and
  // off" instead of rendering a false "module is off" screen during load.
  settingsLoaded: false,
  refreshSettings: () => {},
});

export function SettingsProvider({ children }) {
  const { token, restaurantId } = useAuth();
  const [vatRate,                setVatRate]               = useState(18);
  const [paymentTrackingEnabled, setPaymentTrackingEnabled] = useState(true);
  const [budgetModuleEnabled,    setBudgetModuleEnabled]    = useState(false);
  const [dishCostingEnabled,     setDishCostingEnabled]    = useState(false);
  const [topProductsEnabled,     setTopProductsEnabled]    = useState(false);
  const [settingsLoaded,         setSettingsLoaded]        = useState(false);

  // restaurantId follows the URL synchronously (the api.js interceptor reads
  // the same URL on every request), but the module flags only update once the GET in
  // refreshSettings resolves. If the reset happened in a useEffect instead,
  // it would be too late: React fires effects bottom-up (children before
  // parents) within one commit, so a child like Navbar — whose badge effect
  // is ALSO keyed on restaurantId — would run its effect (reading the
  // still-stale context value from this render) before this provider's own
  // reset effect got a chance to fire, and it would fire
  // GET /api/dishes/alerts/count against the NEW tenant using the OLD
  // module flag. Doing the reset here, during render (the documented "adjust
  // state while rendering" pattern), closes that window: React re-renders
  // this component immediately and synchronously before committing, so every
  // descendant — Navbar included — only ever sees either the previous
  // restaurant's flags (before the id changed) or "reset to false" (the
  // instant it changes), never a stale flag paired with the new tenant.
  // vatRate/paymentTrackingEnabled are deliberately left out of this reset —
  // they don't gate anything, and a wrong VAT default would be worse than a
  // stale one.
  const [flagsRestaurantId, setFlagsRestaurantId] = useState(restaurantId);
  if (flagsRestaurantId !== restaurantId) {
    setFlagsRestaurantId(restaurantId);
    setBudgetModuleEnabled(false);
    setDishCostingEnabled(false);
    setTopProductsEnabled(false);
    setSettingsLoaded(false);
  }

  const refreshSettings = useCallback(() => {
    if (!token) return;
    settingsApi.get()
      .then(r => {
        setVatRate(r.data.vat_rate ?? 18);
        setPaymentTrackingEnabled(!!r.data.payment_tracking_enabled);
        setBudgetModuleEnabled(!!r.data.budget_module_enabled);
        setDishCostingEnabled(!!r.data.dish_costing_enabled);
        setTopProductsEnabled(!!r.data.top_products_enabled);
      })
      .catch(() => {})
      // Loaded either way — a failed fetch must still clear settingsLoaded,
      // otherwise a gate blocked on it (RequireDishModule) hangs blank
      // forever instead of falling back to its closed state.
      .finally(() => setSettingsLoaded(true));
  }, [token, restaurantId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { refreshSettings(); }, [token, restaurantId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <SettingsContext.Provider value={{
      vatRate, paymentTrackingEnabled, budgetModuleEnabled, dishCostingEnabled,
      topProductsEnabled, settingsLoaded, refreshSettings,
    }}>
      {children}
    </SettingsContext.Provider>
  );
}

export const useSettings = () => useContext(SettingsContext);
